import { exportPublicJwk, getOrCreateKeyPair } from './keyStore';

function base64UrlEncode(input: ArrayBuffer | Uint8Array | string): string {
  let bytes: Uint8Array;
  if (typeof input === 'string') {
    bytes = new TextEncoder().encode(input);
  } else if (input instanceof Uint8Array) {
    bytes = input;
  } else {
    bytes = new Uint8Array(input);
  }
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256Base64Url(input: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return base64UrlEncode(digest);
}

function uuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export interface DpopProofOptions {
  htu: string;
  htm: string;
  accessToken?: string;
  nonce?: string;
}

export async function makeDpopProof(opts: DpopProofOptions): Promise<string> {
  const pair = await getOrCreateKeyPair();
  const jwk = await exportPublicJwk(pair);

  const header = {
    typ: 'dpop+jwt',
    alg: 'ES256',
    jwk,
  };

  const payload: Record<string, unknown> = {
    htu: opts.htu,
    htm: opts.htm.toUpperCase(),
    iat: Math.floor(Date.now() / 1000),
    jti: uuid(),
  };
  if (opts.nonce) payload.nonce = opts.nonce;
  if (opts.accessToken) payload.ath = await sha256Base64Url(opts.accessToken);

  const signingInput = base64UrlEncode(JSON.stringify(header)) + '.' + base64UrlEncode(JSON.stringify(payload));

  const signature = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    pair.privateKey,
    new TextEncoder().encode(signingInput),
  );

  return signingInput + '.' + base64UrlEncode(signature);
}
